import React from "react";
import { ArrowUpRight, Calendar, MapPin, CheckCircle2 } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { interdanzaProgram, interdanzaEditions, contactInfo } from "../mock/mock";

const Interdanza = () => {
  const { t, pick } = useLang();

  return (
    <div>
      <PageHero
        image="https://customer-assets.emergentagent.com/job_arte-natura-hub/artifacts/ohwa3qde_DSC8942.jpg"
        kicker={t("interdanza.kicker")}
        title={t("interdanza.title")}
        lead={t("interdanza.lead")}
      />

      {/* CURRENT EDITION */}
      <section className="bg-[#faf7f2] py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_arte-natura-hub/artifacts/6cjvuoha_596820277_1421276956672232_4477619180498129117_n.jpg"
                alt="Interdanza — ballerina, Gala Sulle ali della danza"
                className="w-full aspect-[4/5] object-cover"
              />
              <div className="absolute -bottom-5 -right-2 sm:right-6 bg-[#b5533a] text-white px-6 py-4">
                <span className="block font-serif text-3xl leading-none">2026</span>
                <span className="text-[11px] uppercase tracking-[0.18em]">14ª ed.</span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <span className="inline-flex items-center gap-2 text-[#b5533a] text-xs uppercase tracking-[0.2em]">
              <Calendar className="w-4 h-4" />
              {t("interdanza.currentDates")}
            </span>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl text-[#1c1b1a]">
              {t("interdanza.currentTitle")}
            </h2>
            <p className="mt-5 text-[#3d3a35] leading-relaxed">
              {t("interdanza.currentText")}
            </p>
            <a
              href={contactInfo.interdanza}
              target="_blank"
              rel="noreferrer"
              className="mt-8 inline-flex items-center gap-2 bg-[#1c1b1a] hover:bg-[#2f2c28] text-white px-7 py-3.5 text-sm uppercase tracking-[0.14em] transition-colors"
            >
              {t("interdanza.visitSite")}
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* PROGRAM */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <Reveal className="mb-12">
            <h2 className="font-serif text-4xl md:text-5xl text-[#1c1b1a]">
              {t("interdanza.programTitle")}
            </h2>
          </Reveal>
          <div className="space-y-5">
            {interdanzaProgram.map((p, i) => (
              <Reveal
                key={i}
                delay={i * 70}
                className="flex gap-4 border border-[#eae4d9] p-6 hover:border-[#b5533a] transition-colors"
              >
                <CheckCircle2 className="w-6 h-6 text-[#b5533a] shrink-0" strokeWidth={1.6} />
                <p className="text-[#3d3a35] leading-relaxed">{pick(p)}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EDITIONS TIMELINE */}
      <section className="bg-[#faf7f2] py-20 lg:py-28">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <Reveal className="mb-12 text-center">
            <h2 className="font-serif text-4xl md:text-5xl text-[#1c1b1a]">
              {t("interdanza.editionsTitle")}
            </h2>
            <p className="mt-3 text-[#8a8478] text-sm">{t("interdanza.externalNote")}</p>
          </Reveal>

          <div className="relative pl-6 sm:pl-0">
            <div className="absolute left-[7px] sm:left-1/2 top-2 bottom-2 w-px bg-[#dcd4c6] sm:-translate-x-1/2" />
            <div className="space-y-6">
              {interdanzaEditions.map((e, i) => (
                <Reveal
                  key={e.year}
                  delay={(i % 4) * 60}
                  className={`relative sm:w-1/2 ${
                    i % 2 === 0 ? "sm:pr-10 sm:text-right" : "sm:ml-auto sm:pl-10"
                  }`}
                >
                  <span
                    className={`absolute top-2 w-3.5 h-3.5 rounded-full bg-[#b5533a] ring-4 ring-[#faf7f2] -left-[23px] sm:left-auto ${
                      i % 2 === 0 ? "sm:-right-[7px]" : "sm:-left-[7px]"
                    }`}
                  />
                  <div className="bg-white border border-[#eae4d9] p-5">
                    <span className="font-serif text-2xl text-[#b5533a]">{e.year}</span>
                    <h3 className="mt-1 font-serif text-xl text-[#1c1b1a]">{pick(e.title)}</h3>
                    {e.author && (
                      <p className="mt-0.5 text-sm text-[#8a8478] italic">{e.author}</p>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Interdanza;
